/**
 * Classe utilitaire pour gérer les disponibilités et créneaux horaires
 */
export class AvailabilityTools {
  /**
   * Calcule la durée d'un créneau en minutes
   * @param slot Le créneau avec start et end
   * @returns La durée en minutes
   */
  public static getSlotDurationMinutes(slot: {
    start: string
    end: string
  }): number {
    const start = new Date(slot.start).getTime()
    const end = new Date(slot.end).getTime()
    return Math.round((end - start) / 60000)
  }

  /**
   * Trouve l'événement précédent (finissant avant ou au début du créneau)
   * @param slotStart La date de début du créneau
   * @param sortedEvents Les événements triés par heure de début
   * @returns L'événement précédent et son index, ou null
   */
  public static findPreviousEvent(
    slotStart: Date,
    sortedEvents: any[]
  ): { event: any; index: number } | null {
    for (let i = sortedEvents.length - 1; i >= 0; i--) {
      const eventEnd = new Date(sortedEvents[i].end || sortedEvents[i].endDate)
      if (eventEnd.getTime() <= slotStart.getTime()) {
        return { event: sortedEvents[i], index: i }
      }
    }
    return null
  }

  /**
   * Trouve l'événement suivant (commençant à ou après la fin du créneau)
   * @param slotEnd La date de fin du créneau
   * @param sortedEvents Les événements triés par heure de début
   * @returns L'événement suivant et son index, ou null
   */
  public static findNextEvent(
    slotEnd: Date,
    sortedEvents: any[]
  ): { event: any; index: number } | null {
    for (let i = 0; i < sortedEvents.length; i++) {
      const eventStart = new Date(
        sortedEvents[i].start || sortedEvents[i].startDate
      )
      if (eventStart.getTime() >= slotEnd.getTime()) {
        return { event: sortedEvents[i], index: i }
      }
    }
    return null
  }

  /**
   * Calcule le temps de trajet effectif pour un créneau
   * @param params Les paramètres de calcul
   * @returns Les informations de trajet calculées
   */
  public static calculateSlotTravelConstraints(params: {
    slotStart: Date
    slotEnd: Date
    previousEvent: any | null
    nextEvent: any | null
    travelFromPrevious: number | null
    travelToNext: number | null
    interventionDuration: number
  }): SlotTravelInfo {
    const {
      slotStart,
      slotEnd,
      previousEvent,
      nextEvent,
      travelFromPrevious,
      travelToNext,
      interventionDuration,
    } = params

    const availableMinutes = (slotEnd.getTime() - slotStart.getTime()) / 60000

    const travelFromPrevValue = travelFromPrevious ?? 0
    const travelToNextValue = travelToNext ?? 0

    // Calcul de l'heure d'arrivée effective
    let effectiveStartTime = slotStart.getTime()
    if (previousEvent && travelFromPrevious !== null) {
      const previousEnd = new Date(previousEvent.end || previousEvent.endDate)
      const arrivalTime = previousEnd.getTime() + travelFromPrevValue * 60000
      effectiveStartTime = Math.max(effectiveStartTime, arrivalTime)
    }

    // Calcul de l'heure de départ au plus tard
    let effectiveEndTime = slotEnd.getTime()
    if (nextEvent && travelToNext !== null) {
      const nextStart = new Date(nextEvent.start || nextEvent.startDate)
      const latestDeparture = nextStart.getTime() - travelToNextValue * 60000
      effectiveEndTime = Math.min(effectiveEndTime, latestDeparture)
    }

    const effectiveAvailableMinutes =
      (effectiveEndTime - effectiveStartTime) / 60000
    const totalTravelTime = travelFromPrevValue + travelToNextValue
    const requiredTime = interventionDuration + totalTravelTime
    const isFeasible = effectiveAvailableMinutes >= interventionDuration
    const needsAdjustment =
      !isFeasible && availableMinutes >= interventionDuration

    // Calcul du retard d'arrivée
    const arrivalDelay =
      previousEvent && travelFromPrevious !== null
        ? Math.max(0, (effectiveStartTime - slotStart.getTime()) / 60000)
        : 0

    // Calcul des heures de début/fin des événements adjacents
    let previousEventEnd: Date | null = null
    let estimatedArrival: Date | null = null
    if (previousEvent && travelFromPrevious !== null) {
      previousEventEnd = new Date(previousEvent.end || previousEvent.endDate)
      estimatedArrival = new Date(
        previousEventEnd.getTime() + travelFromPrevValue * 60000
      )
    }

    let nextEventStart: Date | null = null
    let latestDeparture: Date | null = null
    if (nextEvent && travelToNext !== null) {
      nextEventStart = new Date(nextEvent.start || nextEvent.startDate)
      latestDeparture = new Date(
        nextEventStart.getTime() - travelToNextValue * 60000
      )
    }

    return {
      travelFromPrevious,
      travelToNext,
      travelFromHome: null, // Not calculated in frontend fallback
      travelToHome: null, // Not calculated in frontend fallback
      homeDepartureTime: null, // Not calculated in frontend fallback
      homeReturnTime: null, // Not calculated in frontend fallback
      isFirstSlotOfDay: !previousEvent, // True if no previous event
      isLastSlotOfDay: !nextEvent, // True if no next event
      homeDepartureIsTight: false, // Not calculated in frontend fallback
      homeReturnIsLate: false, // Not calculated in frontend fallback
      totalTravelTime,
      availableTime: availableMinutes,
      effectiveAvailableTime: Math.max(0, effectiveAvailableMinutes),
      requiredTime,
      isFeasible,
      needsAdjustment,
      previousEvent,
      nextEvent,
      arrivalDelay,
      previousEventEnd,
      estimatedArrival,
      nextEventStart,
      latestDeparture,
      previousEventDoNotShift: null, // Not available in frontend fallback
      nextEventDoNotShift: null, // Not available in frontend fallback
      scenarioAddedKm: null, // Not available in frontend fallback
      scenarioTotalKm: null, // Not available in frontend fallback
      scenarioAddedMin: null, // Not available in frontend fallback
      requiresShift: false, // Not available in frontend fallback
      shiftedEventId: null,
      shiftedEventTitle: null,
    }
  }

  /**
   * Compte les créneaux réalisables
   * Exclut les shift-slots qui ont leur propre catégorie "décaler"
   * @param slotTravelInfo Map des informations de trajet par index de créneau
   * @param totalSlots Nombre total de créneaux
   * @returns Le nombre de créneaux réalisables
   */
  public static countFeasibleSlots(
    slotTravelInfo: Map<number, SlotTravelInfo>,
    totalSlots: number
  ): number {
    if (slotTravelInfo.size === 0) return totalSlots

    let count = 0
    for (let i = 0; i < totalSlots; i++) {
      const info = slotTravelInfo.get(i)
      // Exclure les shift-slots - ils ont leur propre catégorie "décaler"
      if (info?.requiresShift) continue
      if (!info || info.isFeasible) count++
    }
    return count
  }

  /**
   * Compte les créneaux nécessitant un ajustement
   * @param slotTravelInfo Map des informations de trajet par index de créneau
   * @param totalSlots Nombre total de créneaux
   * @returns Le nombre de créneaux nécessitant ajustement
   */
  public static countAdjustableSlots(
    slotTravelInfo: Map<number, SlotTravelInfo>,
    totalSlots: number
  ): number {
    if (slotTravelInfo.size === 0) return 0

    let count = 0
    for (let i = 0; i < totalSlots; i++) {
      const info = slotTravelInfo.get(i)
      if (info?.needsAdjustment) count++
    }
    return count
  }

  /**
   * Trie les créneaux par coût d'insertion dans la tournée (scénario Valhalla N×N)
   * puis par faisabilité, puis par heure de début.
   *
   * Ordre de tri :
   * 1. Créneaux utilisables (OK + SERRÉ) triés par scenario_added_km croissant
   * 2. Créneaux bloqués (SERRÉ + doNotShift) triés par scenario_added_km croissant
   * 3. Créneaux insuffisants triés par heure
   *
   * @param slots Les créneaux à trier
   * @param slotTravelInfo Les informations de trajet
   * @returns Les créneaux triés avec leur index original
   */
  public static sortSlotsByFeasibility(
    slots: any[],
    slotTravelInfo: Map<number, SlotTravelInfo>
  ): { slot: any; originalIndex: number }[] {
    const slotsWithIndices = slots.map((slot, index) => ({
      slot,
      originalIndex: index,
    }))

    if (slotTravelInfo.size === 0) return slotsWithIndices

    return slotsWithIndices.sort((a, b) => {
      const infoA = slotTravelInfo.get(a.originalIndex)
      const infoB = slotTravelInfo.get(b.originalIndex)

      // Catégoriser : 1=OK, 2=DÉCALER (shift-slot), 3=SERRÉ ajustable, 4=SERRÉ bloqué, 5=insuffisant
      const getCategory = (info: SlotTravelInfo | undefined): number => {
        if (!info || info.isFeasible) {
          if (info?.requiresShift) return 2
          return 1
        }
        if (info.needsAdjustment) {
          const isBlocked =
            info.previousEventDoNotShift === true ||
            info.nextEventDoNotShift === true
          return isBlocked ? 4 : 3
        }
        return 5
      }

      const catA = getCategory(infoA)
      const catB = getCategory(infoB)

      // Groupes : utilisables (OK + DÉCALER + SERRÉ ajustable), bloqués, impossibles
      // Le backend calcule scenarioTotalKm de manière comparable :
      //   - OK : tournée complète avec affair insérée
      //   - DÉCALER : tournée complète avec affair à la place + event décalé en fin de parcours
      // Les deux incluent tous les points → comparable directement par km total.
      const getGroup = (cat: number): number => {
        if (cat <= 3) return 1 // Utilisables (OK, DÉCALER, SERRÉ ajustable)
        if (cat === 4) return 2 // Bloqué (SERRÉ + doNotShift)
        return 3 // Insuffisant
      }

      const groupA = getGroup(catA)
      const groupB = getGroup(catB)

      if (groupA !== groupB) return groupA - groupB

      // Dans le même groupe, trier par km total de la tournée (le plus bas = le meilleur)
      const totalA = infoA?.scenarioTotalKm ?? null
      const totalB = infoB?.scenarioTotalKm ?? null

      if (totalA !== null && totalB !== null) {
        if (totalA !== totalB) return totalA - totalB
      } else if (totalA !== null) {
        return -1
      } else if (totalB !== null) {
        return 1
      }

      // À km total égal, préférer OK avant DÉCALER avant SERRÉ
      if (catA !== catB) return catA - catB

      // Enfin, trier par heure de début
      const startA = new Date(a.slot.start).getTime()
      const startB = new Date(b.slot.start).getTime()
      return startA - startB
    })
  }

  /**
   * Filtre les événements avec des coordonnées valides
   * @param events Les événements à filtrer
   * @returns Les événements avec coordonnées valides
   */
  public static filterEventsWithCoordinates(events: any[]): any[] {
    return events.filter((event) => {
      const lat = event.data?.workSiteLatitude || event.workSiteLatitude
      const lng = event.data?.workSiteLongitude || event.workSiteLongitude
      return lat && lng && +lat !== 0 && +lng !== 0
    })
  }

  /**
   * Trie les événements par heure de début
   * @param events Les événements à trier
   * @returns Les événements triés
   */
  public static sortEventsByStartTime(events: any[]): any[] {
    return [...events].sort(
      (a, b) =>
        new Date(a.start || a.startDate).getTime() -
        new Date(b.start || b.startDate).getTime()
    )
  }

  /**
   * Vérifie si un événement a des coordonnées valides
   * @param event L'événement calendrier
   * @returns true si l'événement a des coordonnées valides
   */
  public static hasValidCoordinates(event: any): boolean {
    const lat = event?.data?.workSiteLatitude || event?.workSiteLatitude
    const lng = event?.data?.workSiteLongitude || event?.workSiteLongitude
    return !!(lat && lng && lat !== 0 && lng !== 0)
  }

  /**
   * Récupère la ville d'un événement
   * @param event L'événement calendrier
   * @returns Le nom de la ville ou une chaîne vide
   */
  public static getEventCity(event: any): string {
    if (!event) return ''
    return (
      event.data?.workSiteCity ||
      event.data?.workSiteLocality ||
      event.workSiteCity ||
      event.workSiteLocality ||
      event.affair?.workSiteCity ||
      event.affair?.workSiteLocality ||
      ''
    )
  }

  /**
   * Découpe les plages de disponibilité en créneaux de durée fixée
   * Par exemple: 8h-18h avec durée 30min => 8h-8h30, 8h30-9h, ..., 17h30-18h
   * @param slots Les plages de disponibilité à découper
   * @param durationMinutes La durée de chaque créneau en minutes
   * @returns Les créneaux découpés
   */
  public static splitSlotsIntoDurationChunks(
    slots: { start: string; end: string; available_minutes?: number }[],
    durationMinutes: number
  ): { start: string; end: string; available_minutes: number }[] {
    if (!slots || slots.length === 0 || durationMinutes <= 0) {
      return []
    }

    const result: { start: string; end: string; available_minutes: number }[] =
      []

    for (const slot of slots) {
      const slotStart = new Date(slot.start)
      const slotEnd = new Date(slot.end)

      // Découper en créneaux de durationMinutes
      let currentStart = slotStart.getTime()
      const endTime = slotEnd.getTime()

      while (currentStart + durationMinutes * 60000 <= endTime) {
        const chunkEnd = currentStart + durationMinutes * 60000

        result.push({
          start: new Date(currentStart).toISOString(),
          end: new Date(chunkEnd).toISOString(),
          available_minutes: durationMinutes,
        })

        currentStart = chunkEnd
      }
    }

    return result
  }
}

/**
 * Interface pour les informations de trajet d'un créneau
 */
export interface SlotTravelInfo {
  travelFromPrevious: number | null
  travelToNext: number | null
  travelFromHome: number | null // Temps de trajet depuis le domicile du technicien
  travelToHome: number | null // Temps de trajet vers le domicile du technicien
  homeDepartureTime: Date | null // Heure de départ du domicile
  homeReturnTime: Date | null // Heure de retour au domicile
  isFirstSlotOfDay: boolean // Premier créneau de la journée (départ du domicile)
  isLastSlotOfDay: boolean // Dernier créneau de la journée (retour au domicile)
  homeDepartureIsTight: boolean // Le départ du domicile est tôt (avant 7h00)
  homeReturnIsLate: boolean // Le retour au domicile est tard (après 19h30)
  totalTravelTime: number
  availableTime: number
  effectiveAvailableTime: number
  requiredTime: number
  isFeasible: boolean
  needsAdjustment: boolean
  previousEvent: any | null
  nextEvent: any | null
  arrivalDelay: number
  previousEventEnd: Date | null
  estimatedArrival: Date | null
  nextEventStart: Date | null
  latestDeparture: Date | null
  // do_not_shift flags for adjacent events - if true, event cannot be shifted
  previousEventDoNotShift: boolean | null // L'événement précédent ne peut pas être décalé
  nextEventDoNotShift: boolean | null // L'événement suivant ne peut pas être décalé
  // Scenario insertion cost (Valhalla N×N route evaluation)
  scenarioAddedKm: number | null // Extra km added by inserting at this position
  scenarioTotalKm: number | null // Total day route km with insertion
  scenarioAddedMin: number | null // Extra minutes added by inserting at this position
  // Shift-slot: requires shifting an existing (unlocked) intervention at this position
  requiresShift: boolean // True if an existing intervention must be moved
  shiftedEventId: number | null // ID of the intervention to shift
  shiftedEventTitle: string | null // Title of the intervention to shift
}
